import { useState, useEffect } from 'react'
import dotloopApi from '../services/dotloopApi'
import { DotloopProfile } from '../models/DotloopTypes'

export default function useDotloopProfiles(isAuthenticated: boolean = false, dotloopEmail: string | null = null) {
  const [profiles, setProfiles] = useState<DotloopProfile[]>([])
  const [selectedProfile, setSelectedProfile] = useState<DotloopProfile | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [needsReauth, setNeedsReauth] = useState(false)
  const allowNonIndividualProfiles = process.env.REACT_APP_DOTLOOP_ALLOW_NON_INDIVIDUAL_PROFILES === 'true'

  useEffect(() => {
    if (isAuthenticated) {
      fetchProfiles()
    } else {
      // Reset state when not authenticated
      setProfiles([])
      setSelectedProfile(null)
      setError(null)
      setNeedsReauth(false)
    }
  }, [isAuthenticated, dotloopEmail])

  const fetchProfiles = async () => {
    setLoading(true)
    setError(null)
    setNeedsReauth(false)
    
    try {
      const profilesList = await dotloopApi.fetchProfiles()
      
      console.log('📋 Raw profiles from API:', profilesList)
      
      // Filter to active profiles
      // Per Dotloop API docs: only INDIVIDUAL profiles can be accessed, OFFICE/COMPANY return 403
      const usableProfiles = profilesList.filter(p => {
        const isUsable = allowNonIndividualProfiles
          ? !p.deactivated
          : p.type === 'INDIVIDUAL' && !p.deactivated
        if (!isUsable) {
          console.log(`⚠️ Filtering out profile "${p.name}" (type: ${p.type}, deactivated: ${p.deactivated})`)
        }
        return isUsable
      })
      
      console.log('✅ Usable profiles:', usableProfiles)
      
      if (usableProfiles.length === 0 && profilesList.length > 0) {
        setError(allowNonIndividualProfiles
          ? 'No active profiles found.'
          : 'No INDIVIDUAL profiles found. Dotloop API only allows access to INDIVIDUAL profiles.'
        )
      }
      
      setProfiles(usableProfiles)
      
      // Auto-select default profile or first profile
      const defaultProfile = usableProfiles.find(p => p.default)
      if (defaultProfile) {
        setSelectedProfile(defaultProfile)
        console.log('🎯 Auto-selected default profile:', defaultProfile.name)
      } else if (usableProfiles.length > 0) {
        setSelectedProfile(usableProfiles[0])
        console.log('🎯 Auto-selected first profile:', usableProfiles[0].name)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch profiles'
      console.error('❌ Error fetching profiles:', err)
      
      // Check if error indicates need for re-authentication
      const isAuthError = 
        errorMessage.toLowerCase().includes('access denied') ||
        errorMessage.toLowerCase().includes('unauthorized') ||
        errorMessage.toLowerCase().includes('re-authenticate') ||
        errorMessage.toLowerCase().includes('token') ||
        errorMessage.toLowerCase().includes('401') ||
        errorMessage.toLowerCase().includes('403')
      
      if (isAuthError) {
        setNeedsReauth(true)
        setError('DotLoop authentication issue. An admin needs to re-authenticate with DotLoop.')
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleProfileChange = (profileId: number) => {
    const profile = profiles.find(p => p.id === profileId)
    setSelectedProfile(profile || null)
  }

  const refreshProfiles = () => {
    fetchProfiles()
  }

  return {
    profiles,
    selectedProfile,
    loading,
    error,
    needsReauth,
    handleProfileChange,
    refreshProfiles,
  }
}

